/**
 * Copy and pure rules for "Add team member" (DNKADM-0491; dink-api DNKAPI-0713).
 *
 * Role words (UI only; backend roles unchanged): Owner = OWNER, Manager = ADMIN, Front desk =
 * STAFF without a staff record, Coach = STAFF with one (created and linked by the flow).
 * Who can add whom: an Owner (or human platform support) any of the four; a Manager Front desk
 * and Coach only. Display only — dink-api refuses the rest (`403 ROLE_NOT_ALLOWED`).
 */
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import type { TeamMemberCalendarStatus, TeamMemberKind } from '@/api/teamMembers';
import { authRefusalMessage } from '@/features/members/unconfirmedOutcome';

export interface KindChoice {
  kind: TeamMemberKind;
  label: string;
  /** One line saying what they can do. */
  hint: string;
}

const ALL_KINDS: readonly KindChoice[] = [
  { kind: 'OWNER', label: 'Owner', hint: 'Everything, at every location, including the team.' },
  {
    kind: 'MANAGER',
    label: 'Manager',
    hint: 'Sets up and runs their locations, and can add front desk staff and coaches.',
  },
  {
    kind: 'FRONT_DESK',
    label: 'Front desk',
    hint: 'Runs the day at their locations: bookings, check-in and members.',
  },
  {
    kind: 'COACH',
    label: 'Coach',
    hint: 'Front desk access, plus a bookable schedule, the services they teach and a calendar.',
  },
];

/** The choices this session may offer: all four for an owner, Front desk and Coach otherwise. */
export function kindChoices(canAddAnyRole: boolean): readonly KindChoice[] {
  return canAddAnyRole
    ? ALL_KINDS
    : ALL_KINDS.filter((c) => c.kind === 'FRONT_DESK' || c.kind === 'COACH');
}

/** A coach's name must carry a first and a last name (the first word is the first name). */
export function hasFirstAndLastName(name: string): boolean {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length >= 2
  );
}

/** How a calendar result line reads, and which fix it offers. */
export interface CalendarResultLine {
  ok: boolean;
  text: string;
  /** `picker`: link an existing calendar here; `staff`: fix it on the staff page. */
  fix: 'picker' | 'staff' | null;
}

/**
 * Step 5 in words (Peyton 11/3): both SKIPPED statuses read "Calendar not linked yet" with the
 * existing-calendar picker; FAILED reads "Calendar not linked" with a Fix link to the staff
 * page. An unknown future status reads like FAILED.
 */
export function calendarResultLine(status: TeamMemberCalendarStatus): CalendarResultLine {
  switch (status) {
    case 'CREATED':
      return { ok: true, text: 'Calendar created', fix: null };
    case 'LINKED':
      return { ok: true, text: 'Calendar linked', fix: null };
    case 'NONE':
      return { ok: true, text: 'Calendar skipped for now', fix: null };
    case 'SKIPPED_NO_SCOPE':
    case 'SKIPPED_NO_GHL_USER':
      return { ok: false, text: 'Calendar not linked yet', fix: 'picker' };
    default:
      return { ok: false, text: 'Calendar not linked', fix: 'staff' };
  }
}

/** "Added to N services" (or that none were picked). */
export function servicesResultLine(count: number): string {
  if (count === 0) return 'No services added yet';
  return `Added to ${count} service${count === 1 ? '' : 's'}`;
}

/** The 409 codes whose outcome is still unknown (the same key is being worked on). */
export function isInFlightRefusal(error: unknown): boolean {
  return v2ErrorCode(error) === 'CONCURRENT_REQUEST';
}

/** The existing invite a `409 INVITATION_ALREADY_*` carries, when it does. */
function existingInviteEmail(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const invitation = error.details?.['invitation'];
  if (typeof invitation !== 'object' || invitation === null) return null;
  const email = (invitation as Record<string, unknown>)['email'];
  return typeof email === 'string' && email.trim().length > 0 ? email.trim() : null;
}

/** The plain line for a refused add. Nothing was added on any of these. */
export function teamMemberErrorMessage(error: unknown, email: string): string {
  const who = existingInviteEmail(error) ?? (email.trim() || 'This person');
  switch (v2ErrorCode(error)) {
    case 'ALREADY_ACTIVE_MEMBER':
      return `${who} is already on the team.`;
    case 'INVITATION_ALREADY_PENDING':
      return `${who} already has an invite waiting. Resend it from the Invited tab.`;
    case 'INVITATION_ALREADY_ACCEPTED':
      return `${who} already accepted an invite and is on the team.`;
    case 'ROLE_NOT_ALLOWED':
      return 'Managers can add Front desk and Coach only. Ask an owner to add an Owner or Manager.';
    case 'LOCATION_ACCESS_DENIED':
      return 'You can only give access to your own locations, and pick services offered there.';
    case 'CALENDAR_NOT_OFFERED':
      return 'That calendar can’t be linked. Pick another one, or choose Create one for them.';
    case 'DUPLICATE_NAME':
      return 'A staff member with this name already exists. Use a different name.';
    case 'NOT_FOUND':
      return 'A location or service you picked is no longer available. Close this and try again.';
    case 'INVALID_REQUEST':
      return serverMessage(error) ?? 'Check the details and try again.';
    default:
      break;
  }
  return (
    authRefusalMessage(error) ?? serverMessage(error) ?? 'The team member wasn’t added. Try again.'
  );
}

/** Shown while an add the server never confirmed is held under its key. */
export const UNCONFIRMED_TEAM_MEMBER_MESSAGE =
  "We couldn't confirm the team member was added. Check again re-sends the same request; nothing is added twice.";

/** Asked before closing the drawer with an unconfirmed add. */
export const UNCONFIRMED_TEAM_MEMBER_CLOSE_PROMPT =
  "We couldn't confirm the team member was added. If you close now and add them again, they may get two invites. Close anyway?";
